import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { MP_LABEL, forcedValueLabel, ruPlural, severityColor } from './msProductsModel';

const OWN_TEXT = new Set(['MS_ARCHIVED', 'MS_MISSING', 'DUPLICATE_CARD_SAME_CABINET', 'CARD_ARCHIVED', 'ACCOUNT_INACTIVE', 'NO_WAREHOUSE_BINDING', 'STOCK_NOT_TRANSLATED']);

function accountText(account) {
  if (!account) return '—';
  const mp = MP_LABEL[account.marketplace] || account.marketplace || '';
  return `${mp ? `${mp} · ` : ''}${account.name || account.id}${account.active === false ? ' (неактивен)' : ''}`;
}

function Barcodes({ label, list, highlight }) {
  const set = new Set((highlight || []).map((b) => String(b).toLowerCase()));
  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center">
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>{label}</Typography>
      {(list || []).length
        ? list.map((bc) => (
          <Chip
            key={bc}
            size="small"
            variant="outlined"
            color={set.has(String(bc).toLowerCase()) ? 'success' : 'default'}
            label={bc}
            sx={{ height: 20, fontFamily: 'monospace', fontSize: 11 }}
          />
        ))
        : <Typography variant="caption" color="text.disabled">нет</Typography>}
    </Stack>
  );
}

function Line({ label, children }) {
  return (
    <Stack direction="row" spacing={1} alignItems="baseline">
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>{label}</Typography>
      <Typography variant="body2" component="div" sx={{ minWidth: 0, wordBreak: 'break-word' }}>{children}</Typography>
    </Stack>
  );
}

function DuplicateCards({ details, actions, busy }) {
  const cards = details?.cards || [];
  return (
    <Stack spacing={1}>
      <Typography variant="body2">
        В кабинете {accountText(details?.account)} к этому товару привязана ещё {ruPlural(cards.length, 'карточка', 'карточки', 'карточек')}, каждой уходит одинаковый остаток.
        {' '}Обычно трансляция должна быть включена у одной карточки, остальные — выключить или удалить.
      </Typography>
      {details?.enabled_total > 1 && (
        <Alert severity="warning" sx={{ py: 0 }}>Трансляция включена у {details.enabled_total} карточек этого кабинета</Alert>
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Карточка</TableCell>
            <TableCell>Ключ</TableCell>
            <TableCell>Трансляция</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {cards.map((card) => (
            <TableRow key={`${card.marketplace}:${card.product_key}`}>
              <TableCell sx={{ maxWidth: 260 }}>
                <Typography variant="body2" noWrap title={card.name || ''}>{card.article || card.name || '—'}</Typography>
                {card.article && card.name && (
                  <Typography variant="caption" color="text.secondary" noWrap component="div" title={card.name}>{card.name}</Typography>
                )}
                {!card.card_found && <Chip size="small" color="error" variant="outlined" label="нет карточки" sx={{ height: 18, fontSize: 10 }} />}
                {card.card_archived && <Chip size="small" color="error" variant="outlined" label="архив МП" sx={{ height: 18, fontSize: 10 }} />}
              </TableCell>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{card.product_key}</TableCell>
              <TableCell>
                <Switch
                  size="small"
                  checked={!!card.enabled}
                  disabled={busy}
                  onChange={(e) => actions.setLinkEnabled(card, e.target.checked)}
                  inputProps={{ 'aria-label': 'Трансляция карточки' }}
                />
              </TableCell>
              <TableCell align="right">
                <Button size="small" color="error" disabled={busy} onClick={() => actions.removeLink(card)}>Удалить</Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
}

/** Пояснение и действия по одной проблеме (issue.details с бэка). */
export default function MsIssueDetails({ issue, labels, link, product, actions, busy }) {
  const meta = labels?.[issue.code] || {};
  const d = issue.details || {};
  const archived = !!product?.ms_archived;
  const msArchivedText = link
    ? 'Связь указывает на архивный товар МойСклад — синк остатков её пропускает.'
    : `Товар в архиве МойСклад. Его связи синк остатков пропускает: выключите или удалите их${(product?.rebind_targets || []).length ? ', либо перенесите на живой товар с тем же ШК' : ''}.`;
  const body = (() => {
    switch (issue.code) {
      case 'DUPLICATE_CARD_SAME_CABINET':
        return <DuplicateCards details={d} actions={actions} busy={busy} />;
      case 'CARD_MISSING':
        return (
          <Stack spacing={0.5}>
            <Line label="Кабинет">{accountText(d.account)}</Line>
            <Line label="Ключ связи"><code>{d.product_key || link?.product_key}</code></Line>
            <Typography variant="body2" color="text.secondary">
              Карточки с таким ключом нет в каталоге кабинета: удалена на МП или привязан не тот ключ.
            </Typography>
          </Stack>
        );
      case 'CARD_ARCHIVED':
        return (
          <Typography variant="body2">
            Карточка {d.cards?.[0]?.article || link?.article || link?.product_key} в кабинете {accountText(d.account)} в архиве маркетплейса —
            остаток по ней не уходит. Выключите трансляцию или удалите связь.
          </Typography>
        );
      case 'REBIND_AVAILABLE':
        return (
          <Stack spacing={0.5}>
            <Line label="Живой товар">
              <Button size="small" sx={{ p: 0, minWidth: 0, textTransform: 'none' }} onClick={() => actions.selectProduct(d.target_ms?.ms_id)}>
                {d.target_ms?.name || d.target_ms?.ms_id}
              </Button>
            </Line>
            <Barcodes label="ШК карточки" list={d.barcodes?.card} highlight={d.target_ms?.barcodes} />
            <Barcodes label="ШК живого" list={d.target_ms?.barcodes} highlight={d.barcodes?.card} />
          </Stack>
        );
      case 'BARCODE_MISMATCH':
        return (
          <Stack spacing={0.5}>
            <Barcodes label="ШК товара МС" list={d.barcodes?.ms} />
            <Barcodes label="ШК карточки" list={d.barcodes?.card} />
            <Typography variant="body2" color="text.secondary">
              Ни один ШК не совпал — проверьте, та ли карточка привязана. Если карточка верная, обновите ШК в МС или на МП.
            </Typography>
          </Stack>
        );
      case 'ACCOUNT_INACTIVE':
        return (
          <Typography variant="body2">
            Кабинет {accountText(d.account)} выключен в настройках кабинетов МП — синк остатков его пропускает.
          </Typography>
        );
      case 'NO_WAREHOUSE_BINDING':
        return (
          <Typography variant="body2">
            У кабинета {accountText(d.account)} нет складской связки МС↔МП — включённая связь ничего не отправляет.
            Создайте связку в «Склады» → «Связь с МП».
          </Typography>
        );
      case 'PARENT_WITH_VARIANTS':
        return (
          <Stack spacing={0.5}>
            <Typography variant="body2">
              Остаток в МС лежит на модификациях ({d.variants_count}) — привяжите карточку к нужной модификации.
            </Typography>
            <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
              {(d.variants || []).map((v) => (
                <Chip key={v.ms_id} size="small" variant="outlined" label={v.name || v.ms_id} onClick={() => actions.selectProduct(v.ms_id)} />
              ))}
            </Stack>
          </Stack>
        );
      case 'MS_ARCHIVED':
      case 'MS_MISSING':
        return (
          <Stack spacing={0.5}>
            <Typography variant="body2">
              {issue.code === 'MS_ARCHIVED'
                ? msArchivedText
                : 'Товара нет в МойСклад — синк остатков связь пропускает.'}
            </Typography>
            {d.target_ms && (
              <Line label={link ? 'По ШК карточки' : 'Живой по ШК'}>
                <Button size="small" sx={{ p: 0, minWidth: 0, textTransform: 'none' }} onClick={() => actions.selectProduct(d.target_ms.ms_id)}>
                  {d.target_ms.name || d.target_ms.ms_id}
                </Button>
              </Line>
            )}
          </Stack>
        );
      case 'FORCED_ORPHAN':
        return (
          <Stack spacing={0.5}>
            <Line label="Связка">{d.binding?.missing ? `удалена (id ${d.binding.id})` : (d.binding?.name || d.binding?.id)}</Line>
            <Line label="Кабинет">{accountText(d.account)}</Line>
            <Line label="Режим">
              {d.mode === 'live' ? 'живой остаток' : d.mode === 'none' ? 'не транслировать' : `принуд ${forcedValueLabel(d.forced_stock, '—')}`}
            </Line>
            <Typography variant="body2" color="text.secondary">
              {d.binding?.missing
                ? 'Складская связка удалена — настройка никуда не уходит.'
                : 'Товар не привязан к этому кабинету — настройка никуда не уходит.'}
            </Typography>
          </Stack>
        );
      case 'STOCK_NOT_TRANSLATED':
        return (
          <Stack spacing={0.5}>
            <Line label="Кабинет">{accountText(d.account)}</Line>
            <Line label="Связки">
              {(d.bindings || []).map((b) => `${b.name || b.id}${b.source === 'binding' ? ' (по умолчанию)' : ''}`).join(', ') || '—'}
            </Line>
            <Typography variant="body2" color="text.secondary">
              На всех складских связках кабинета для этого товара режим «не транслировать» — на МП уходит 0.
              Если товар должен продаваться, задайте «живой остаток» или принуд на нужной связке (например, КГТ или МГТ).
            </Typography>
          </Stack>
        );
      case 'CANDIDATE_UNLINKED_CARD': {
        const card = d.cards?.[0] || {};
        return (
          <Stack spacing={0.5}>
            <Line label="Кабинет">{accountText(d.account)}</Line>
            <Line label="Карточка">{card.article || card.name || card.product_key} <code>{card.product_key}</code></Line>
            <Barcodes label="ШК карточки" list={d.barcodes?.card} highlight={d.barcodes?.ms} />
          </Stack>
        );
      }
      default:
        return null;
    }
  })();

  const buttons = (() => {
    const out = [];
    const add = (key, label, onClick, color) => out.push({ key, label, onClick, color });
    switch (issue.code) {
      case 'CARD_MISSING':
        if (link) {
          add('rebind', 'Перепривязать', () => actions.openLinkDialog({ marketplace: link.marketplace, account_id: link.account_id }));
          add('del', 'Удалить связь', () => actions.removeLink(link), 'error');
        }
        break;
      case 'CARD_ARCHIVED':
      case 'ACCOUNT_INACTIVE':
        if (link?.enabled) add('off', 'Выключить', () => actions.setLinkEnabled(link, false), 'warning');
        if (link) add('del', 'Удалить связь', () => actions.removeLink(link), 'error');
        break;
      case 'REBIND_AVAILABLE':
        if (link && d.target_ms) add('rebind', 'Перепривязать', () => actions.openRebind(link, d.target_ms), 'primary');
        break;
      case 'BARCODE_MISMATCH':
        if (link) add('del', 'Удалить связь', () => actions.removeLink(link), 'error');
        break;
      case 'NO_WAREHOUSE_BINDING':
        add('stores', 'Склады', () => actions.openStores());
        break;
      case 'MS_ARCHIVED':
      case 'MS_MISSING':
        if (!link) {
          add('off-all', 'Выключить все связи', () => actions.archivedBulk('disable'), 'warning');
          add('del-all', 'Удалить все связи', () => actions.archivedBulk('delete'), 'error');
          if ((product?.rebind_targets || []).length) add('move', 'Перенести связи на живой товар', () => actions.archivedBulk('transfer'), 'primary');
        } else {
          if (d.target_ms) add('rebind', 'Перепривязать', () => actions.openRebind(link, d.target_ms), 'primary');
          if (link.enabled) add('off', 'Выключить', () => actions.setLinkEnabled(link, false), 'warning');
          add('del', 'Удалить связь', () => actions.removeLink(link), 'error');
        }
        break;
      case 'FORCED_ORPHAN':
        if (d.binding?.id != null) add('clear', 'Снять настройку', () => actions.clearForced(d.binding.id), 'warning');
        break;
      case 'STOCK_NOT_TRANSLATED':
        add('forced', 'По складам', () => actions.openForced(), 'primary');
        break;
      case 'CANDIDATE_UNLINKED_CARD': {
        const card = d.cards?.[0];
        if (card && !archived) {
          add('link', 'Привязать', () => actions.openLinkDialog({
            marketplace: card.marketplace,
            account_id: card.account_id,
            product_key: card.product_key,
          }), 'primary');
        }
        break;
      }
      default:
        break;
    }
    return out;
  })();

  return (
    <Box
      sx={{
        borderLeft: 3,
        borderColor: `${severityColor(issue.severity) === 'default' ? 'grey' : severityColor(issue.severity)}.main`,
        pl: 1.5,
        py: 0.5,
      }}
    >
      {meta.description && !OWN_TEXT.has(issue.code) && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: body ? 0.75 : 0 }}>{meta.description}</Typography>
      )}
      {body}
      {buttons.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
          {buttons.map((b) => (
            <Button key={b.key} size="small" variant="outlined" color={b.color || 'inherit'} disabled={busy} onClick={b.onClick}>
              {b.label}
            </Button>
          ))}
        </Stack>
      )}
    </Box>
  );
}
